import React from 'react';
import '../styles/App.css';
import '../styles/responsive.css';
import SmallFooter from '../components/SmallFooter';
import BrandLink from '../components/BrandLink';
import { LINKS } from '../constants';

function TermsOfService() {
  return (
	<main>
	  <DefaultSection />
	</main>
  );
}

const DefaultSection = () => {
  return (
	<>
	  <section className="legal-page">
		<div className="legal-container">
		  <h1>Terms of Service</h1>
		  <p className="legal-effective">Effective Date: May 2026</p>
		  <div>

			<p>
			  Welcome to Barista Bot (the "Service"), a product of{' '}
			  <BrandLink href={LINKS.espressoPlusMilk}>
				Espresso + Milk, LLC
			  </BrandLink>{' '}
			  ("we," "us," or "our"). By creating an account, placing an order,
			  or otherwise using the Service, you agree to these Terms of
			  Service ("Terms") and the company-wide{' '}
			  <BrandLink href={LINKS.espressoTerms}>
				Espresso + Milk Terms
			  </BrandLink>
			  . If you do not agree, do not use the Service.
			</p>

			<h2>Eligibility</h2>
			<p>
			  You must be at least 18 years old, or the age of majority in your
			  jurisdiction, to use the Service. You represent that the
			  information you provide is accurate and that you have the legal
			  authority to enter into these Terms.
			</p>

			<h2>Use of Service</h2>
			<p>
			  Barista Bot allows you to place beverage orders and manage your
			  account via SMS and this web portal. You must provide a valid US
			  mobile phone number to use the Service. The Service is intended
			  for personal, non-commercial use.
			</p>

			<h2>Account</h2>
			<p>
			  You are responsible for maintaining the security of your account
			  and for all activity that occurs under it. Do not share your
			  one-time login codes with anyone. Notify us immediately at{' '}
			  <a href="mailto:hi@baristabot.app">hi@baristabot.app</a> if you
			  suspect unauthorized use of your account.
			</p>

			<h2>Acceptable Use</h2>
			<p>You agree not to:</p>
			<ul>
			  <li>
				Use the Service for any unlawful, fraudulent, or harmful
				purpose.
			  </li>
			  <li>
				Place orders with no intention of paying or with stolen payment
				methods.
			  </li>
			  <li>
				Harass, threaten, or abuse our staff, participating coffee
				shops, or their employees.
			  </li>
			  <li>
				Impersonate any person or misrepresent your affiliation with
				any entity.
			  </li>
			  <li>
				Use bots, scrapers, or other automated means to access or
				interact with the Service.
			  </li>
			  <li>
				Reverse engineer, decompile, or attempt to extract source code
				from the Service.
			  </li>
			  <li>
				Interfere with or disrupt the integrity or performance of the
				Service.
			  </li>
			</ul>

			<h2>Orders and Payments</h2>
			<p>
			  Orders are processed through Square. By placing an order, you
			  authorize the charge to your payment method on file. Pricing,
			  availability, and menu items are set by the participating coffee
			  shop and may change without notice. Your relationship for the
			  preparation and quality of the beverage is with the participating
			  coffee shop. Payment processing is also governed by{' '}
			  <a
				href="https://squareup.com/legal/general-terms"
				target="_blank"
				rel="noopener noreferrer"
			  >
				Square's terms
			  </a>
			  .
			</p>

			<h2>Refunds</h2>
			<p>
			  All sales are final unless otherwise agreed by the participating
			  coffee shop. If a drink is incorrect, missing, or unsatisfactory,
			  please contact the store directly for resolution. We may, at our
			  discretion, assist with disputes but are not obligated to issue
			  refunds for orders prepared by third-party shops.
			</p>

			<h2>SMS Messaging Terms</h2>
			<p>
			  <strong>Program name:</strong> Barista Bot (operated by{' '}
			  <BrandLink href={LINKS.espressoPlusMilk}>
				Espresso + Milk, LLC
			  </BrandLink>
			  ).
			</p>
			<p>
			  <strong>Description:</strong> Barista Bot is a text-based coffee
			  ordering and account-management service.
			</p>
			<p>
			  <strong>How to opt in:</strong> You opt in by (a) entering your
			  mobile phone number on baristabot.app, my.baristabot.app, or
			  account.baristabot.app and confirming the consent checkbox; (b)
			  texting a participating coffee shop's published Barista Bot
			  number; or (c) replying <strong>START</strong> or{' '}
			  <strong>YES</strong> to a verification message we send you.
			</p>
			<p>
			  <strong>Message types you will receive:</strong>
			</p>
			<ul>
			  <li>
				One-time login codes used to authenticate your account.
			  </li>
			  <li>
				Order confirmations, preparation status, and pickup-ready
				notifications.
			  </li>
			  <li>
				Customer-support replies in response to your inquiries.
			  </li>
			  <li>
				Optional promotional messages ("Drops") from participating
				coffee shops if you separately opt in.
			  </li>
			</ul>
			<p>
			  <strong>Message frequency:</strong> Message frequency varies
			  based on your activity. You should expect roughly 1–10 messages
			  per week during normal use, and additional messages if you opt in
			  to promotional Drops from one or more coffee shops.
			</p>
			<p>
			  <strong>Cost:</strong> Message and data rates may apply. Barista
			  Bot does not charge for SMS messages, but your wireless carrier
			  may.
			</p>
			<p>
			  <strong>Opt out (STOP):</strong> Reply <strong>STOP</strong> to
			  any message to opt out of non-essential SMS. You will receive a
			  final confirmation message such as: "You have been unsubscribed
			  from Barista Bot. No more messages will be sent. Reply START to
			  re-subscribe." Some account-critical messages (for example,
			  login codes you actively request) may still be sent.
			</p>
			<p>
			  <strong>Help (HELP):</strong> Reply <strong>HELP</strong> to any
			  message for support. You will receive a reply such as: "Barista
			  Bot support: email hi@baristabot.app or visit baristabot.app.
			  Msg & data rates may apply. Reply STOP to unsubscribe."
			</p>
			<p>
			  <strong>Supported carriers:</strong> Barista Bot SMS is supported
			  on major US carriers including AT&amp;T, Verizon, T-Mobile, and
			  their MVNOs. Carriers are not liable for delayed or undelivered
			  messages.
			</p>
			<p>
			  <strong>Customer care:</strong> For help with SMS or your
			  account, email{' '}
			  <a href="mailto:hi@baristabot.app">hi@baristabot.app</a>.
			</p>
			<p>
			  <strong>Privacy:</strong> Your mobile phone number, opt-in
			  status, and consent records are not shared with third parties or
			  affiliates for marketing or promotional purposes. See our{' '}
			  <a href="/privacy-policy">Privacy Policy</a> for full details.
			</p>

			<h2>Intellectual Property</h2>
			<p>
			  The Service, including its name, logo, software, and content
			  (excluding user-submitted content and coffee-shop menu data), is
			  owned by{' '}
			  <BrandLink href={LINKS.espressoPlusMilk}>
				Espresso + Milk, LLC
			  </BrandLink>{' '}
			  and protected by intellectual
			  property laws. We grant you a limited, non-exclusive,
			  non-transferable, revocable license to use the Service for its
			  intended purpose.
			</p>

			<h2>Termination</h2>
			<p>
			  We may suspend or terminate your access to the Service at any
			  time, with or without notice, for any reason, including violation
			  of these Terms. You may close your account at any time by
			  emailing <a href="mailto:hi@baristabot.app">hi@baristabot.app</a>.
			  Sections that by their nature should survive termination
			  (including Limitation of Liability, Indemnification, and
			  Governing Law) will survive.
			</p>

			<h2>Disclaimer of Warranties</h2>
			<p>
			  TO THE FULLEST EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED
			  ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY
			  KIND, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION
			  MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
			  NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE
			  UNINTERRUPTED, ERROR-FREE, OR SECURE.
			</p>

			<h2>Limitation of Liability</h2>
			<p>
			  TO THE FULLEST EXTENT PERMITTED BY LAW, ESPRESSO + MILK, LLC AND
			  ITS OFFICERS, MEMBERS, EMPLOYEES, AND AGENTS WILL NOT BE LIABLE
			  FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
			  DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUES, DATA, OR
			  GOODWILL, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE
			  SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR
			  RELATING TO THESE TERMS OR THE SERVICE WILL NOT EXCEED THE
			  GREATER OF (A) THE AMOUNT YOU PAID TO US IN THE TWELVE (12)
			  MONTHS PRECEDING THE CLAIM, OR (B) FIFTY DOLLARS ($50).
			</p>

			<h2>Indemnification</h2>
			<p>
			  You agree to defend, indemnify, and hold harmless Espresso +
			  Milk, LLC and its affiliates from any claims, damages, or
			  expenses (including reasonable attorneys' fees) arising from your
			  use of the Service, your violation of these Terms, or your
			  violation of any law or third-party right.
			</p>

			<h2>Governing Law and Venue</h2>
			<p>
			  These Terms are governed by the laws of the State of Oklahoma,
			  without regard to its conflict-of-laws principles. Subject to the
			  Dispute Resolution section below, you agree to the exclusive
			  jurisdiction of the state and federal courts located in Oklahoma
			  County, Oklahoma.
			</p>

			<h2>Dispute Resolution</h2>
			<p>
			  Any dispute, claim, or controversy arising out of or relating to
			  these Terms or the Service that cannot be resolved informally
			  will be resolved through binding individual arbitration
			  administered by the American Arbitration Association under its
			  Consumer Arbitration Rules, in Oklahoma County, Oklahoma. YOU AND
			  WE EACH WAIVE THE RIGHT TO A JURY TRIAL AND THE RIGHT TO
			  PARTICIPATE IN A CLASS ACTION. You may opt out of this
			  arbitration agreement by emailing{' '}
			  <a href="mailto:hi@baristabot.app">hi@baristabot.app</a> within
			  30 days of first accepting these Terms.
			</p>

			<h2>Changes to These Terms</h2>
			<p>
			  We may update these Terms at any time. Material changes will be
			  communicated via SMS, email, or notice in the customer portal.
			  Continued use of the Service after changes take effect
			  constitutes acceptance of the updated Terms.
			</p>

			<h2>Miscellaneous</h2>
			<p>
			  These Terms, together with our{' '}
			  <a href="/privacy-policy">Privacy Policy</a>, constitute the
			  entire agreement between you and{' '}
			  <BrandLink href={LINKS.espressoPlusMilk}>
				Espresso + Milk, LLC
			  </BrandLink>{' '}
			  regarding
			  the Service. If any provision is found unenforceable, the
			  remaining provisions will remain in full effect. Our failure to
			  enforce any right is not a waiver. You may not assign these
			  Terms; we may assign them in connection with a merger,
			  acquisition, or sale of assets.
			</p>

			<h2>Contact</h2>
			<p>
			  Questions? Email{' '}
			  <a href="mailto:hi@baristabot.app">hi@baristabot.app</a>.
			</p>
			<p>
			  <BrandLink href={LINKS.espressoPlusMilk}>
				Espresso + Milk, LLC
			  </BrandLink>
			  <br />
			  219 NW 12th St, Suite 23<br />
			  Oklahoma City, OK 73103
			</p>
		  </div>
		</div>
	  </section>
	  <SmallFooter />
	</>
  );
};

export default TermsOfService;
